<script setup>
import {router, usePage} from "@inertiajs/vue3"
import {computed, onMounted, ref, watch} from "vue"
import {eventBus} from "@/Services/eventBus.js"
import CLink from "@/Components/CLink.vue"
import CLoginBox from "@/Components/CLoginBox.vue"
import PCart from "@/Layouts/Partials/PCart.vue"
import EToast from "@/Components/EToast.vue"
import CDialog from "@/Components/CDialog.vue"

const items = [
    {src: 'institucional.home', title: 'Conheça-nos'},
    {src: 'institucional.historia', title: 'Nossa História'},
    {src: 'institucional.missao', title: 'Missão, Visão e Valores'},
    {src: 'institucional.personalizacoes', title: 'Personalizações'},
    {src: 'institucional.faq', title: 'FAQ'},
]

const props = defineProps({
    cart: Boolean
})

const drawer = ref(false)
const mobileMenuDrawer = ref(false)
const dialog = ref()
const disableSearch = ref(false)
const page = usePage()
const urlSearchTerm = computed(() => page.props.searchTerm)
const searchTerm = ref(page.props.searchTerm)
const searchModal = ref(false)
const suggestedCategories = ref([])
const isLoading = ref(false)
const mobileSearchActive = ref(false)

let timeout, lastSearch = ''

const submitSearch = function () {
    lastSearch = searchTerm.value
    const filters = route().params
    delete filters.product;

    delete filters.page // sempre que for feita uma busca, resetamos para a primeira página
    delete filters.category // sempre que fizermos uma busca, resetamos a categoria. todo - https://creativedesignbr.atlassian.net/browse/DEV-285
    delete filters.filters;

    if (searchTerm.value === undefined) {
        return
    }

    if (typeof searchTerm.value === 'string' && searchTerm.value.trim() !== '') {
        filters.search = searchTerm.value
    } else if (filters.search) {
        return
    }

    filters.slug = 'produtos'

    router.get(route('product.list', filters, {preserveState: true}), {}, {
            onSuccess: () => {
                searchModal.value = false
                mobileSearchActive.value = false
            },
        }
    )
}

const fetchSuggestedCategories = async (term) => {
    if (term.length < 3) {
        suggestedCategories.value = []
        return
    }

    isLoading.value = true
    try {
        const response = await axios.get(route('category.searchByTag'), {
            params: {term}
        })
        suggestedCategories.value = response.data.categories

    } catch (error) {
        console.error('Erro ao buscar categorias:', error)
        suggestedCategories.value = []
    } finally {
        isLoading.value = false
    }
}

const navigateToCategory = (category) => {
    if (!category || !category.slug) return

    router.get(route('product.category', category.slug), {}, {
        onBefore: () => {
            disableSearch.value = true
        },
        onFinish: () => {
            searchTerm.value = ''
            disableSearch.value = false
        }
    })
}

const handleOpenCart = () => {
    drawer.value = true
}

const handleLogout = () => {
    router.post(route('logout'))
}

const user = computed(() => page.props.auth.user)

onMounted(() => {
    eventBus.on('openCart', handleOpenCart)
})

// update searchTerm when url changes
watch(urlSearchTerm, function (search) {
    searchTerm.value = search || ''
})

watch(searchTerm, function (search) {
        if (timeout)
            clearTimeout(timeout)

        if (search?.length >= 3) {
            timeout = setTimeout(() => {
                fetchSuggestedCategories(search)

                if (search !== lastSearch) {
                }
            }, 200)
        } else {
            suggestedCategories.value = []
        }
    }
)
</script>

<template>
    <v-sheet v-if="!$vuetify.display.mobile" color="primary" class="i-header-desktop">
        <v-row class="i-header">
            <v-col class="i-header-image" cols="2">
                <c-link :href="route('home')">
                    <v-img :src="page.props.logoUrl"/>
                </c-link>
            </v-col>
            <v-col></v-col>
            <v-col cols="3" class="mr-15">
                <template v-slot:default>
                    <div class="w-100 d-flex justify-center align-baseline">
                        <v-icon
                            class="mr-1"
                            color="headerTextColor"
                            icon="mdi-magnify"
                            @click="
                                () => {
                                    if (searchTerm) {
                                        submitSearch()
                                    }
                                }
                            "
                        />
                        <v-autocomplete
                            v-model="searchTerm"
                            :disabled="disableSearch"
                            :items="suggestedCategories"
                            item-title="title"
                            item-value="id"
                            :search-input="searchTerm"
                            @update:search="value => searchTerm = value"
                            @update:model-value="navigateToCategory"
                            @keydown.enter="submitSearch"
                            :loading="isLoading"
                            no-filter
                            hide-no-data
                            return-object
                            hide-details
                            placeholder="Buscar produtos..."
                            variant="underlined"
                            density="comfortable"
                            color="headerTextColor"
                            base-color="headerTextColor"
                            class="mt-5"
                            style="max-width: 50vw;"
                        />
                    </div>
                </template>
            </v-col>
            <v-col cols="4" class="align-center d-flex font-weight-bold i-header-menu">
                <v-row>
                    <v-col class="d-flex justify-end me-10">
                        <v-menu>
                            <template v-slot:activator="{ props }">
                                <span v-bind="props">
                                    Sobre nós <span class="mdi mdi-menu-down" style="font-size: 1rem"></span>
                                </span>
                            </template>
                            <v-list>
                                <v-list-item v-for="(item, index) in items" :key="index" :value="index">
                                    <v-list-item-action @click="router.get(route(item.src))">{{ item.title }}</v-list-item-action>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                        <CLink class="ms-5" :href="route('product.list')">Catálogo</CLink>
                        <CLink class="ms-5" :href="route('institucional.contato')">Contato</CLink>
                    </v-col>
                    <div class="d-flex flex-column align-start gap-1 ml-5">
                        <v-btn id="loginBoxActivator" variant="text" size="medium" class="mb-1">
                            <v-menu activator="#loginBoxActivator" :close-on-content-click="false">
                                <v-sheet class="pa-5" width="400" rounded>
                                    <c-login-box/>
                                </v-sheet>
                            </v-menu>
                            <v-icon color="headerTextColor" :icon="user ? 'mdi-menu' : 'mdi-account'"/>
                            <span class="font-weight-bold">{{ user ? 'Menu' : 'Login' }}</span>
                        </v-btn>
                        <v-btn @click.stop="drawer = !drawer" variant="text" size="medium" :disabled="!props.cart">
                            <v-badge v-if="page.props.cartItems.length > 0" :content="page.props.cartItems.length" color="headerTextColor" overlap>
                                <v-icon color="headerTextColor">mdi-shopping</v-icon>
                            </v-badge>
                            <v-icon v-else color="headerTextColor">mdi-shopping</v-icon>
                            <span class="font-weight-bold">Orçamento</span>
                        </v-btn>
                    </div>
                </v-row>
            </v-col>
        </v-row>
    </v-sheet>

    <v-app-bar
        v-if="$vuetify.display.mobile"
        color="primary"
        elevation="0"
        height="70"
        class="i-header-mobile"
        flat
        fixed
    >
        <div class="mobile-header-container">
            <!-- Logo Mobile -->
            <div class="mobile-logo-wrapper">
                <c-link :href="route('home')">
                    <v-img :src="page.props.logoUrl" height="40" width="70" contain class="mr-0"/>
                </c-link>
            </div>

            <div v-if="mobileSearchActive" class="mobile-search-container">
                <v-text-field
                    v-model="searchTerm"
                    placeholder="Buscar produtos..."
                    variant="plain"
                    density="compact"
                    hide-details
                    autofocus
                    class="mobile-search-input"
                    @keydown.enter="submitSearch"
                />
                <v-btn
                    icon="mdi-close"
                    variant="text"
                    color="headerTextColor"
                    size="small"
                    @click="mobileSearchActive = false"
                    class="mobile-search-close"
                />
            </div>

            <v-btn
                v-if="!mobileSearchActive"
                icon
                @click="mobileSearchActive = true"
                color="headerTextColor"
                class="mobile-search-icon"
            >
                <v-icon>mdi-magnify</v-icon>
            </v-btn>

            <!-- Botão Menu Mobile -->
            <v-app-bar-nav-icon
                @click="mobileMenuDrawer = !mobileMenuDrawer"
                color="headerTextColor"
                class="mobile-menu-icon"
            />
        </div>
    </v-app-bar>

    <!-- Navigation Drawer Mobile -->
    <v-navigation-drawer
        v-model="mobileMenuDrawer"
        temporary
        location="end"
        width="280"
        class="i-mobile-menu"
        :scrim="true"
        close-on-content-click
    >
        <v-list nav class="pa-0 mobile-menu-list">
            <!-- Header do Menu -->
            <v-sheet color="primary">
                <v-list-item class="pa-4">

                    <v-list-item-title class="headerTextColor font-weight-bold">
                        Menu
                    </v-list-item-title>

                    <template v-slot:append>
                        <v-btn
                            icon="mdi-close"
                            variant="text"
                            color="headerTextColor"
                            size="small"
                            @click="mobileMenuDrawer = false"
                        />
                    </template>
                </v-list-item>
            </v-sheet>
            <!-- Login Box -->
            <v-list-item class="px-4 py-2">
                <c-login-box/>
            </v-list-item>
            <v-divider class="my-2"/>
            <!-- Carrinho Mobile -->
            <v-list-item v-if="cart" @click.stop="drawer = !drawer; mobileMenuDrawer = false" class="px-4 pt-3 mobile-nav-link">
                <CLink class="mobile-link-wrapper">
                    <v-badge v-if="page.props.cartItems.length > 0" :content="page.props.cartItems.length" color="red" overlap class="mr-3 my-2">
                        <v-icon color="rgb(78, 78, 78)">mdi-shopping</v-icon>
                    </v-badge>
                    <v-icon v-else class="mr-3" color="rgb(78, 78, 78)">mdi-shopping</v-icon>
                    <v-list-item-title>Orçamento</v-list-item-title>
                </CLink>
            </v-list-item>

            <v-divider class="my-2"/>


            <!-- Catálogo -->
            <v-list-item @click="mobileMenuDrawer = false" class="px-4 py-2 mobile-nav-link">
                <CLink :href="route('home')" class="mobile-link-wrapper">
                    <v-icon class="mr-3" color="rgb(78, 78, 78)">mdi-view-grid</v-icon>
                    <span class="mobile-link-text">Catálogo</span>
                </CLink>
            </v-list-item>

            <v-divider class="my-2"/>

            <!-- Sobre - Link único para mobile -->
            <v-list-item @click="mobileMenuDrawer = false" class="px-4 py-2 mobile-nav-link">
                <CLink :href="route('institucional.home')" class="mobile-link-wrapper">
                    <v-icon class="mr-3" color="rgb(78, 78, 78)">mdi-information</v-icon>
                    <v-list-item-title class="mobile-link-text">Sobre</v-list-item-title>
                </CLink>
            </v-list-item>

            <!-- Nossa História -->
            <v-list-item @click="mobileMenuDrawer = false" class="px-4 py-2 mobile-nav-link">
                <CLink :href="route('institucional.historia')" class="mobile-link-wrapper">
                    <v-icon class="mr-3" color="rgb(78, 78, 78)">mdi-book-open-page-variant</v-icon>
                    <v-list-item-title class="mobile-link-text">Nossa História</v-list-item-title>
                </CLink>
            </v-list-item>

            <!-- Missão, Visão e Valores -->
            <v-list-item @click="mobileMenuDrawer = false" class="px-4 py-2 mobile-nav-link">
                <CLink :href="route('institucional.missao')" class="mobile-link-wrapper">
                    <v-icon class="mr-3" color="rgb(78, 78, 78)">mdi-bullseye-arrow</v-icon>
                    <v-list-item-title class="mobile-link-text">Missão, Visão e Valores</v-list-item-title>
                </CLink>
            </v-list-item>

            <!-- Personalizações -->
            <v-list-item @click="mobileMenuDrawer = false" class="px-4 py-2 mobile-nav-link">
                <CLink :href="route('institucional.personalizacoes')" class="mobile-link-wrapper">
                    <v-icon class="mr-3" color="rgb(78, 78, 78)">mdi-brush-variant</v-icon>
                    <v-list-item-title class="mobile-link-text">Personalizações</v-list-item-title>
                </CLink>
            </v-list-item>

            <!-- Contato -->
            <v-list-item @click="mobileMenuDrawer = false" class="px-4 py-2 mobile-nav-link">
                <CLink :href="route('institucional.contato')" class="mobile-link-wrapper">
                    <v-icon class="mr-3" color="rgb(78, 78, 78)">mdi-email</v-icon>
                    <span class="mobile-link-text">Contato</span>
                </CLink>
            </v-list-item>

            <!-- Botão de Sair no final do menu -->
            <v-list-item class="logout-button px-4 py-2" v-if="user">
                <v-btn
                    block
                    color="error"
                    variant="flat"
                    prepend-icon="mdi-logout"
                    @click="handleLogout"
                >Sair
                </v-btn>
            </v-list-item>
        </v-list>
    </v-navigation-drawer>
    <!-- Carrinho -->
    <p-cart v-model="drawer"/>

    <v-dialog v-model="searchModal" max-width="400">
        <v-card>
            <v-card-title class="d-flex align-center pa-3" color="primary">
                <span class="headerTextColor text-subtitle-1">Buscar</span>
                <v-spacer></v-spacer>
                <v-btn
                    icon="mdi-close"
                    variant="text"
                    color="headerTextColor"
                    size="small"
                    @click="searchModal = false"
                />
            </v-card-title>
            <v-card-text class="pa-3">
                <v-form @submit.prevent="submitSearch" class="d-flex flex-column">
                    <v-text-field
                        v-model="searchTerm"
                        placeholder="Digite sua busca..."
                        variant="outlined"
                        density="compact"
                        hide-details
                        autofocus
                        clearable
                        append-inner-icon="mdi-magnify"
                        @click:append-inner="submitSearch"
                        class="mb-2"
                    />
                    <div class="d-flex justify-end gap-2">
                        <v-btn @click="searchModal = false" variant="text" size="small">Cancelar</v-btn>
                        <v-btn color="primary" type="submit" variant="flat" size="small">Buscar</v-btn>
                    </div>
                </v-form>
            </v-card-text>
        </v-card>
    </v-dialog>

    <c-dialog ref="dialog"/>
    <e-toast/>
</template>

<style scoped>
* {
    box-sizing: border-box;
}

.i-header-desktop {
    overflow: hidden;
    width: 100vw !important;
    margin: 0 !important;
    padding: 0 !important;
}

.i-header {
    background-color: transparent !important;
}

.i-header-image .v-img {
    width: 125px !important;
    min-height: 51px !important;
    margin: 1.2rem 5rem;
}

.i-header-menu {
    color: #4e4e4e;
    margin-right: 3%;
}

.i-header-menu .c-link {
    color: #4e4e4e;
}

.i-header .v-btn {
    margin-top: 2px;
}

.i-header .v-btn span {
    font-size: 0.8rem;
    padding-left: 2px;
    color: #4e4e4e;
}

.i-header-mobile {
    width: 100vw !important;
    max-width: 100vw !important;
    left: 0 !important;
    right: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    position: fixed !important;
    top: 0 !important;
    z-index: 1005 !important;
}

.mobile-header-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 70px;
    padding: 0 8px;
    position: relative;
}

.mobile-logo-wrapper {
    flex: 0 0 auto;
    padding-left: 0;
    margin-right: 10px;
    margin-left: 10px;
}

.mobile-search-icon {
    position: absolute;
    right: 48px;
    margin: 0;
    z-index: 2;
}

.mobile-menu-icon {
    position: absolute;
    right: 8px;
    margin: 0;
    z-index: 3;
}

.mobile-search-container {
    display: flex;
    align-items: center;
    position: absolute;
    left: 40%;
    right: 56px;
    z-index: 1;
    background-color: inherit;
    transition: all 0.3s ease;
}

.mobile-search-input {
    flex: 1;
}

.mobile-search-input :deep(.v-field__input) {
    padding: 8px 0;
    color: #4e4e4e;
}

.mobile-search-input :deep(.v-field) {
    border-radius: 0;
    background: transparent;
}

.mobile-search-close {
    margin-left: 4px;
}

.i-mobile-menu {
    z-index: 2001 !important;
    position: fixed !important;
    max-width: 280px !important;
    width: 280px !important;
    height: 100vh !important;
    top: 0 !important;
    right: 0 !important;
}

:deep(.i-mobile-menu .v-list-item .c-login-box) {
    width: 100% !important;
    padding: 0 !important;
}

.mobile-nav-link {
    min-height: 48px !important;
    border-radius: 0 !important;
}

.mobile-link-wrapper {
    text-decoration: none !important;
    color: inherit !important;
    width: 100% !important;
    display: flex !important;
    align-items: center !important;
    font-family: "Poppins", sans-serif !important;
}

.mobile-link-text {
    font-family: "Poppins", sans-serif !important;
    font-size: 1rem !important;
    font-weight: 400 !important;
    color: rgb(78, 78, 78) !important;
    line-height: 1.5 !important;
}

:deep(.v-list-item-title) {
    font-family: "Poppins", sans-serif !important;
    font-size: 1rem !important;
    font-weight: 400 !important;
    color: rgb(78, 78, 78) !important;
    line-height: 1.5 !important;
}

:deep(.v-navigation-drawer) {
    max-width: 280px !important;
    width: 280px !important;
    position: fixed !important;
    right: 0 !important;
    top: 0 !important;
    bottom: 0 !important;
    transform: translateX(100%) !important;
    transition: transform 0.3s ease !important;
}

.mobile-menu-list {
    display: flex !important;
    flex-direction: column !important;
    height: 100% !important;
}

.logout-button {
    margin-top: auto !important;
    margin-bottom: 16px !important;
}

:deep(.v-navigation-drawer--active) {
    transform: translateX(0) !important;
}

:deep(.v-overlay__scrim) {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    z-index: 2000 !important;
    background: rgb(0 0 0 / 42%) !important;
}

:deep(.v-app-bar) {
    width: 100vw !important;
    max-width: 100vw !important;
    left: 0 !important;
    right: 0 !important;
    position: fixed !important;
}


@media (max-width: 960px) {
    .mobile-header-container {
        padding: 0 4px;
    }

    .mobile-search-container {
        left: 30%;
        right: 52px;
    }

    .mobile-search-icon {
        right: 44px;
    }

    .mobile-menu-icon {
        right: 4px;
    }
}

@media (max-width: 600px) {
    .mobile-header-container {
        padding: 0 2px;
    }

    .mobile-search-container {
        left: 95px;
        right: 48px;
    }

    .mobile-search-icon {
        right: 40px;
    }

    .mobile-menu-icon {
        right: 2px;
    }
}

@media screen and (max-width: 100vw) {
    .i-header-mobile,
    .i-header-desktop {
        overflow-x: hidden !important;
        max-width: 100vw !important;
    }

    body, html {
        overflow-x: hidden !important;
        max-width: 100vw !important;
    }
}

.v-list-item {
    border-radius: 0 !important;
    min-height: 48px !important;
}

.v-list-item:hover {
    background-color: rgba(0, 0, 0, 0.04) !important;
}

.v-list-item a {
    text-decoration: none !important;
    color: rgb(78, 78, 78) !important;
    width: 100% !important;
}

:deep(.v-list-group .v-list-item) {
    padding-left: 32px !important;
}

:deep(.v-list-group__items .v-list-item) {
    background-color: rgb(78, 78, 78) !important;
}
</style>
